import StencilHTMLElement from "./stencil-element";

class DiscountApplicator extends StencilHTMLElement {
  name = "DiscountApplicator";

  form;
  applyButton;
  input;

  #events = null;
  #busy = false;

  connectedCallback() {
    this.form = this.querySelector("#discount-applicator-form");
    this.applyButton = this.querySelector(".btn-apply");
    this.input = this.querySelector(".input-discount");

    this.#bind();
  }

  disconnectedCallback() { this.#unbind(); }

  #bind() {
    this.#unbind();
    this.#events = new AbortController();
    const { signal } = this.#events;

    // Submit covers button click + Enter; form has onsubmit="return false" already
    this.form.addEventListener("submit", () => this.#onApply(), { signal });

    // Delegated badge remove (works after DOM re-renders)
    this.addEventListener("click", e => {
      const remove = e.target.closest(".discount-remove");
      if (!remove) return;
      const code = remove.closest("[data-code]").dataset.code;
      this.#onRemove(code);
    }, { signal });
  }

  #unbind() { 
    if (this.#events) this.#events.abort(), this.#events = null;
  }

  #getCurrentDiscounts() {
    return [...this.querySelectorAll(".badge-discount[data-code]")].map(el => el.dataset.code);
  }

  #parseEnteredCodes() {
    return (this.input.value || "").trim().split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
  }

  #setBusy(on) {
    this.#busy = on;
    this.setAttribute("aria-busy", on ? "true" : "false");
    this.applyButton.disabled = on;
    this.input.disabled = on;
  }

  #announce(text) {
    const live = this.querySelector(".discount-aria-live");
    if (live) live.textContent = text;
  }

  async #onApply() {
    if (this.#busy) return;

    const entered = this.#parseEnteredCodes();
    if (!entered.length) {
      this.#announce("Enter a discount code.");
      return;
    }

    const current = this.#getCurrentDiscounts();
    const next = [...current, ...entered.filter(code => !current.includes(code))];

    try {
      this.#setBusy(true);

      // Capture response JSON
      const result = await window.MP.Extensions.Cart.setDiscounts(next);

      // Find invalid codes from the ones just entered
      const invalid = (result.discount_codes || [])
        .filter(dc => entered.includes(dc.code) && dc.applicable === false)
        .map(dc => dc.code);

      if (invalid.length) {
        console.warn(this.logPrefix, "Invalid discount codes:", invalid);
        invalid.forEach(code => MP.Utilities.alert("danger", `Code '${code}' is not valid`, "far fa-exclamation-circle"));
      } else {
        this.#announce(
          entered.length > 1 ? "Discounts applied." : `Code ${entered[0]} applied.`
        );
        this.input.value = "";
      }
    } catch (err) {
      console.error(this.logPrefix, "Apply discount failed:", err);
      this.#announce("Couldn’t apply discount. Please try again.");
    } finally {
      this.#setBusy(false);
    }
  }

  async #onRemove(code) {
    if (this.#busy) return;

    const next = this.#getCurrentDiscounts().filter(c => c !== code);

    try {
      this.#setBusy(true);
      await window.MP.Extensions.Cart.setDiscounts(next);
      this.#announce(`Removed code ${code}.`);
    } catch (err) {
      console.error(this.logPrefix, "Remove discount failed:", err);
      this.#announce("Couldn’t remove discount. Please try again.");
    } finally {
      this.#setBusy(false);
    }
  }
}

export default DiscountApplicator;